import { action, makeObservable, observable } from 'mobx';

/**
 * I-0958 (`docs/concept-work-area-scope.md`): the one place the web app's stores learn that the work
 * area a page is about has changed, before the page renders.
 *
 * The stores are singletons that outlive a route. A store used to drop another work area's rows
 * only when a load started, and the load is started by the page's effect, after its first render.
 * So the first render after a switch drew the previous work area's rows under the new one's name.
 * `WorkAreaScopeGate` enters the new work area here, and renders the page only once every
 * registered store has entered it.
 */
export interface IWorkAreaScoped {
  /**
   * Drop every row that belongs to another work area, so the next load fetches. Rows that already
   * belong to `workAreaId` are kept: a return to the same work area reads nothing again (T-0318).
   */
  enterWorkArea(workAreaId: string): void;
}

export class WorkAreaScope {
  /** The work area the registered stores are on, or `null` before the first page about one. */
  current: string | null = null;

  private readonly scoped: IWorkAreaScoped[];

  constructor(members: readonly IWorkAreaScoped[]) {
    this.scoped = [...members];
    makeObservable(this, { current: observable, enter: action, join: action });
  }

  /** The registered stores, those that joined late included. */
  get members(): readonly IWorkAreaScoped[] {
    return this.scoped;
  }

  /** Enter `workAreaId`: every registered store drops another work area's rows, in one action. */
  enter(workAreaId: string): void {
    if (this.current === workAreaId) return;
    for (const member of this.scoped) member.enterWorkArea(workAreaId);
    this.current = workAreaId;
  }

  /**
   * T-0770 (LC-6c): a store that loads after the launch (`workItemStoreInstance.ts`) registers when it
   * loads, and takes up the work area the scope is already on, so it never draws another's rows.
   */
  join(member: IWorkAreaScoped): void {
    if (this.scoped.includes(member)) return;
    this.scoped.push(member);
    if (this.current !== null) member.enterWorkArea(this.current);
  }
}

export type WorkAreaStoreKindType = 'scoped' | 'keyed' | 'global';

/**
 * Every store `stores/index.ts` exports, by what it holds (WS-4). `workAreaScope.guard.test.ts`
 * refuses a store missing here, and a `scoped` one that is not registered with the scope.
 *
 * - `scoped`: one work area's rows at a time; registered, and implements `enterWorkArea`.
 * - `keyed`: rows kept per work area or per entity, so nothing can show under another work area.
 * - `global`: about no work area.
 */
export const WORK_AREA_STORE_KINDS: Readonly<
  Record<string, { kind: WorkAreaStoreKindType; reason: string }>
> = {
  workItemStore: { kind: 'scoped', reason: 'The item list, its groups, search and next up.' },
  decisionsStore: { kind: 'scoped', reason: 'Decisions for me, for one scope at a time.' },
  tasksStore: { kind: 'scoped', reason: 'Tasks for me, for one scope at a time.' },
  invitationsWaitingStore: {
    kind: 'global',
    reason: "The person's own pending invitations (I-2233): about no work area.",
  },
  viewStore: { kind: 'scoped', reason: 'The saved views of the work area it last read.' },
  dashboardPageStore: { kind: 'keyed', reason: 'Pages, checkpoints and errors by work area id.' },
  viewHierarchyStore: {
    kind: 'keyed',
    reason: 'The custom-view tree (F-0363 slice 2) by work area id.',
  },
  openBranchesStore: { kind: 'keyed', reason: 'Open branches in a map by work area id.' },
  commentStore: { kind: 'keyed', reason: 'Comments by entity.' },
  feedbackStore: { kind: 'keyed', reason: 'Read only as a detail by id (FeedbackDetailPage).' },
  aiSuggestionStore: { kind: 'keyed', reason: 'Suggestions by item.' },
  authStore: { kind: 'global', reason: 'The signed-in reader.' },
  projectStore: {
    kind: 'global',
    reason: 'Every work area the reader can open, and the selection.',
  },
  namespaceStore: { kind: 'global', reason: 'The namespace tree.' },
  appearanceStore: {
    kind: 'global',
    reason: 'A person’s Accents, Quiet or Colour (F-0579), about no work area.',
  },
  waitingAnimationStore: {
    kind: 'global',
    reason:
      'A person’s Waiting animation, Random or one study (F-0578 WAIT-5), about no work area.',
  },
  viewChoicesStore: {
    kind: 'global',
    reason:
      'A person’s view choices across devices (F-0527), keyed by scope inside it, not rows of one work area.',
  },
  notificationStore: { kind: 'global', reason: "The reader's notifications, across work areas." },
  workItemViewStore: {
    kind: 'global',
    reason: "The list's display settings; its selection is the address's (T-0318).",
  },
  labelStore: { kind: 'global', reason: 'No page reads it.' },
  decisionAnswerToastStore: {
    kind: 'global',
    reason: 'IU-8: the answer toast, held past the navigation it causes — not any one work area.',
  },
  itemArchiveToastStore: {
    kind: 'global',
    reason:
      'IU-9 (F-0510, item 4): the archive Undo toast, held past the navigation it causes — not any one work area, same as the decision answer toast above.',
  },
  itemCreatedToastStore: {
    kind: 'global',
    reason:
      'IU-10 (F-0510): the Added toast, held past the navigation Add itself causes — not any one work area, same as the archive toast above.',
  },
};
